import { Route, Routes } from "react-router-dom";
import { lazyView, useAuth, type LazyView } from "@/platform/client";
import { MaskProvider } from "@/platform/client";
import { TimezoneProvider } from "@/platform/client";
import { NotificationsProvider } from "@/notifications/client";
import { ModalStackProvider } from "@/platform/client";
import { ConfirmProvider, RecurrenceScopeProvider } from "@/platform/ui";
import { AppShell } from "@/shell/layout/AppShell.js";
import { Spinner } from "@/platform/ui";
import { LoginPage } from "@/shell/LoginPage.js";
import { CashFlowPage } from "@/finance/client";
import { CategorizePage } from "@/finance/client";
import { AccountsPage } from "@/finance/client";
import { StrategyPage } from "@/finance/client";
import { AnalyticsPage } from "@/finance/client";
import { MarketPage } from "@/finance/client";
import { PortfolioPage } from "@/finance/client";
import { CalendarPage } from "@/planning/client";
import { TasksPage } from "@/planning/client";
import { FeedsPage } from "@/feeds/client";
import { ArtGalleryPage } from "@/library/client";
import { HealthOverviewPage, HealthLogPage } from "@/health/client";
import { DataPage } from "@/ingestion/client";
import { NotificationsPage } from "@/notifications/client";
import { ExtensionsPage } from "@/extensions/client";
import { NotFoundPage } from "@/shell/NotFoundPage.js";
import "@/shell/presenters.js";

const NotesPage = lazyView(() => import("@/shell/NotesPage.js").then((m) => m.NotesPage));
const SettingsPage = lazyView(() => import("@/shell/SettingsPage.js").then((m) => m.SettingsPage));

/** Every page by the path it opens at; the shell loads each ahead once the
 *  first has settled. */
const PAGES: [path: string, Page: LazyView][] = [
  ["/", MarketPage],
  ["/portfolio", PortfolioPage],
  ["/accounts", AccountsPage],
  ["/cash-flow", CashFlowPage],
  ["/categorize", CategorizePage],
  ["/strategy", StrategyPage],
  ["/analytics", AnalyticsPage],
  ["/calendar", CalendarPage],
  ["/tasks", TasksPage],
  ["/notes", NotesPage],
  ["/feeds", FeedsPage],
  ["/art-gallery", ArtGalleryPage],
  ["/activity", HealthOverviewPage],
  ["/tracking", HealthLogPage],
  ["/data", DataPage],
  ["/notifications", NotificationsPage],
  ["/extensions", ExtensionsPage],
  ["/settings", SettingsPage],
];
const PRELOADED = PAGES.map(([, Page]) => Page);

// The page the application opens at loads while the session is checked.
void PAGES.find(([path]) => path === window.location.pathname)?.[1]
  .preload()
  .catch(() => undefined);

export function App() {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Spinner label="Starting Waypoint…" />
      </div>
    );
  }

  if (!authenticated) return <LoginPage />;

  return (
    <TimezoneProvider>
      <MaskProvider>
        <NotificationsProvider>
          <ModalStackProvider>
            <ConfirmProvider>
              <RecurrenceScopeProvider>
                <Routes>
                  <Route element={<AppShell pages={PRELOADED} />}>
                    {PAGES.map(([path, Page]) => (
                      <Route key={path} path={path} element={<Page />} />
                    ))}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </RecurrenceScopeProvider>
            </ConfirmProvider>
          </ModalStackProvider>
        </NotificationsProvider>
      </MaskProvider>
    </TimezoneProvider>
  );
}
